// src/lib/targetSpecification.ts
//
// BekalNanti — Milestone P3: Target Specifications Logic
// Pure deterministic helper functions, types, and readiness evaluation.
// Zero AI, zero external APIs, purely deterministic and unit-testable.

import { getGoalDomain } from './goalDomainRegistry'

export type TargetReadinessStatus = 'incomplete' | 'ready_for_costing'

export interface TargetSpecificationRecord {
  id?: string
  goalId: string
  targetStage?: string | null
  programName?: string | null
  targetCountry?: string | null
  targetRegion?: string | null
  targetCity?: string | null
  institutionId?: string | null
  institutionName?: string | null
  institutionType?: string | null
  targetYear?: number | null
  targetDate?: string | null
  durationMonths?: number | null
  targetCurrency?: string
  createdAt?: string
  updatedAt?: string
}

export interface TargetReadinessResult {
  status: TargetReadinessStatus
  isComplete: boolean
  completenessScore: number // 0 to 100 percentage
  completedFieldsCount: number
  totalRequiredFields: number
  missingFields: string[]
  summaryText: string
}

// Canonical Education Stage Options with Default Durations
export interface StageOption {
  id: string
  label: string
  shortLabel: string
  defaultDurationMonths: number
  description: string
}

export const EDUCATION_STAGES: StageOption[] = [
  {
    id: 's1',
    label: 'S1 / Sarjana',
    shortLabel: 'S1',
    defaultDurationMonths: 48,
    description: 'Pendidikan sarjana perguruan tinggi (umumnya 4 tahun)',
  },
  {
    id: 's2',
    label: 'S2 / Magister',
    shortLabel: 'S2',
    defaultDurationMonths: 24,
    description: 'Pendidikan pascasarjana / master (umumnya 2 tahun)',
  },
  {
    id: 'diploma',
    label: 'Diploma (D3 / D4)',
    shortLabel: 'Diploma',
    defaultDurationMonths: 36,
    description: 'Pendidikan vokasi terapan (3-4 tahun)',
  },
  {
    id: 'sma',
    label: 'SMA / SMK',
    shortLabel: 'SMA/SMK',
    defaultDurationMonths: 36,
    description: 'Pendidikan menengah atas atau kejuruan (3 tahun)',
  },
  {
    id: 'smp',
    label: 'SMP',
    shortLabel: 'SMP',
    defaultDurationMonths: 36,
    description: 'Pendidikan menengah pertama (3 tahun)',
  },
  {
    id: 'sd',
    label: 'SD / Sederajat',
    shortLabel: 'SD',
    defaultDurationMonths: 72,
    description: 'Pendidikan dasar (6 tahun)',
  },
  {
    id: 'tk',
    label: 'TK / PAUD',
    shortLabel: 'TK',
    defaultDurationMonths: 24,
    description: 'Pendidikan anak usia dini / taman kanak-kanak (1-2 tahun)',
  },
  {
    id: 'course',
    label: 'Pelatihan & Sertifikasi',
    shortLabel: 'Pelatihan',
    defaultDurationMonths: 12,
    description: 'Kursus profesional, bootcamp, atau sertifikasi keahlian',
  },
]

// Common Fields of Study / Programs (Non-exclusive, user can enter custom text)
export const COMMON_PROGRAMS = [
  'Kedokteran & Kesehatan',
  'Teknik Informatika & Ilmu Komputer',
  'Teknik Sipil / Mesin / Elektro',
  'Bisnis, Manajemen & Keuangan',
  'Akuntansi & Perpajakan',
  'Hukum & Hubungan Internasional',
  'Desain Komunikasi Visual & Seni Kreatif',
  'Psikologi & Ilmu Sosial',
  'Sains & Matematika',
  'Pendidikan & Keguruan',
  'Umum / Belum Ditentukan',
]

// Common Country Options
export const COUNTRY_OPTIONS = [
  { id: 'Indonesia', label: 'Indonesia (Dalam Negeri)', currency: 'IDR' },
  { id: 'Singapura', label: 'Singapura', currency: 'SGD' },
  { id: 'Malaysia', label: 'Malaysia', currency: 'MYR' },
  { id: 'Australia', label: 'Australia', currency: 'AUD' },
  { id: 'Inggris', label: 'Inggris (UK)', currency: 'GBP' },
  { id: 'Amerika Serikat', label: 'Amerika Serikat (US)', currency: 'USD' },
  { id: 'Jepang', label: 'Jepang', currency: 'JPY' },
  { id: 'Jerman', label: 'Jerman / Eropa', currency: 'EUR' },
  { id: 'Lainnya', label: 'Negara Lainnya', currency: 'USD' },
]

// Institution Type Options
export const INSTITUTION_TYPES = [
  { id: 'public', label: 'Negeri (PTN / Sekolah Negeri)' },
  { id: 'private', label: 'Swasta Nasional' },
  { id: 'international', label: 'Internasional / Luar Negeri' },
  { id: 'kedinasan', label: 'Kedinasan / Ikatan Dinas' },
  { id: 'unknown', label: 'Belum Ditentukan' },
]

/**
 * Deterministic Target Readiness Evaluation.
 * 
 * For P4 Costing to be possible without guessing, a target specification
 * requires 4 critical dimensions:
 * 1. targetStage (Jenjang pendidikan)
 * 2. targetCountry (Negara / scope lokasi)
 * 3. targetYear (Tahun mulai / horizon)
 * 4. programName (Bidang studi / fokus)
 *
 * Specific university name, exact city, and exact intake month are optional/unknown-friendly.
 */
export function evaluateTargetReadiness(
  target: Partial<TargetSpecificationRecord> | null | undefined,
  goalType: string = 'education'
): TargetReadinessResult {
  const domain = getGoalDomain(goalType)
  const isEdu = domain.goalType === 'education'
  const stageLabel = isEdu ? 'Jenjang pendidikan' : domain.targetStageLabel
  const progLabel = isEdu ? 'Bidang studi / peminatan' : domain.programNameLabel
  const locLabel = isEdu ? 'Negara atau lingkup lokasi' : domain.locationLabel
  const yearLabel = isEdu ? 'Target tahun mulai' : domain.targetYearLabel

  if (!target) {
    return {
      status: 'incomplete',
      isComplete: false,
      completenessScore: 0,
      completedFieldsCount: 0,
      totalRequiredFields: 4,
      missingFields: [
        `${stageLabel} belum ditentukan`,
        `${progLabel} belum ditentukan`,
        `${locLabel} belum dipilih`,
        `${yearLabel} belum ditentukan`,
      ],
      summaryText: 'Belum ada spesifikasi sasaran yang dicatat.',
    }
  }

  const missingFields: string[] = []
  let completed = 0
  const totalRequired = 4

  // 1. Stage Check
  if (target.targetStage && target.targetStage.trim().length > 0) {
    completed++
  } else {
    missingFields.push(`${stageLabel} belum ditentukan`)
  }

  // 2. Program / Field Check
  if (target.programName && target.programName.trim().length > 0) {
    completed++
  } else {
    missingFields.push(`${progLabel} belum ditentukan`)
  }

  // 3. Country / Location Check (City, Region, or Country qualifies as valid location)
  const hasLocation = Boolean(
    (target.targetCity && target.targetCity.trim().length > 0) ||
    (target.targetCountry && target.targetCountry.trim().length > 0) ||
    (target.targetRegion && target.targetRegion.trim().length > 0)
  )
  if (hasLocation) {
    completed++
  } else {
    missingFields.push(`${locLabel} belum dipilih`)
  }

  // 4. Year Check (either targetYear or targetDate with valid year)
  let hasValidYear = false
  if (typeof target.targetYear === 'number' && target.targetYear >= 2000 && target.targetYear <= 2100) {
    hasValidYear = true
  } else if (target.targetDate) {
    const parsedYear = new Date(target.targetDate).getFullYear()
    if (!isNaN(parsedYear) && parsedYear >= 2000 && parsedYear <= 2100) {
      hasValidYear = true
    }
  }

  if (hasValidYear) {
    completed++
  } else {
    missingFields.push(`${yearLabel} belum ditentukan`)
  }

  const completenessScore = Math.round((completed / totalRequired) * 100)
  const isComplete = completed === totalRequired

  let summaryText = ''
  if (isComplete) {
    const stageObj = domain.stages.find((s) => s.id === target.targetStage) || EDUCATION_STAGES.find((s) => s.id === target.targetStage)
    const stageName = stageObj ? stageObj.shortLabel : target.targetStage
    const locName = (domain.showInstitution ? target.targetCountry : (target.targetCity || target.targetCountry)) || target.targetRegion || ''
    summaryText = `${stageName} ${target.programName} di ${locName} (${target.targetYear || ''})`
  } else if (completed > 0) {
    summaryText = `Spesifikasi parsial (${completed}/${totalRequired} data kunci terisi)`
  } else {
    summaryText = 'Belum ada spesifikasi sasaran yang dicatat.'
  }

  return {
    status: isComplete ? 'ready_for_costing' : 'incomplete',
    isComplete,
    completenessScore,
    completedFieldsCount: completed,
    totalRequiredFields: totalRequired,
    missingFields,
    summaryText,
  }
}

/**
 * Format target specification into human-readable compact badge / summary line.
 */
export function formatTargetSummary(
  target: Partial<TargetSpecificationRecord> | null | undefined,
  goalType: string = 'education'
): string {
  if (!target) return 'Belum dipetakan'

  const domain = getGoalDomain(goalType)
  const parts: string[] = []
  if (target.targetStage) {
    const stageObj = domain.stages.find((s) => s.id === target.targetStage) || EDUCATION_STAGES.find((s) => s.id === target.targetStage)
    parts.push(stageObj ? stageObj.shortLabel : target.targetStage)
  }
  if (target.programName) {
    parts.push(target.programName)
  }
  if (target.targetCity) {
    parts.push(target.targetCity)
  } else if (target.targetCountry) {
    parts.push(target.targetCountry)
  }
  if (target.targetYear) {
    parts.push(`Thn ${target.targetYear}`)
  }

  return parts.length > 0 ? parts.join(' • ') : 'Belum dipetakan'
}
